import { useState } from 'react';
import { Link } from 'react-router-dom';
import ScrollToTop from '@/components/ScrollToTop';
import {
  Wallet, ArrowRight, CheckCircle, MessageSquare, Sparkles,
  Shield, BarChart3, TrendingUp, Flame, Target, Zap, Send,
  Menu, X, Brain, Receipt, Plane, Lock, Download,
  BookOpen, ChevronDown, ChevronUp, Star,
} from 'lucide-react';
import ParticleField from '@/components/landing/ParticleField';
import ChatMockup3D from '@/components/landing/ChatMockup3D';
import CountUpStat from '@/components/landing/CountUpStat';

/* ─── CSS vars ───────────────────────────────────────────────────────── */
const PAGE_VARS = `
  :root {
    --orange: #FF6B00;
    --orange-glow: rgba(255,107,0,0.15);
    --orange-dim: rgba(255,107,0,0.06);
    --black: #05080f;
    --surface: #0d1117;
    --surface-2: #161b22;
    --border: rgba(255,255,255,0.07);
    --text: #f0ede8;
    --muted: #8b949e;
  }
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Outfit:wght@700;800&display=swap');
  @import url('https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@800,700&display=swap');
  @keyframes marquee    { from{transform:translateX(0)} to{transform:translateX(-50%)} }
  @keyframes floatY     { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
  @keyframes fadeUp     { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes glowPulse  { 0%,100%{box-shadow:0 0 0 1px rgba(255,107,0,0.4),0 4px 24px rgba(255,107,0,0.35)} 50%{box-shadow:0 0 0 1px rgba(255,107,0,0.6),0 4px 40px rgba(255,107,0,0.55)} }
  @keyframes cardShine  { 0%{left:-100%} 100%{left:200%} }
  @keyframes typeCursor { 0%,100%{opacity:1} 50%{opacity:0} }
  .fade-up   { animation: fadeUp 0.6s cubic-bezier(.22,1,.36,1) both; }
  .float-y   { animation: floatY 4s ease-in-out infinite; }
  .glow-pulse { animation: glowPulse 2.5s ease-in-out infinite; }
  .feature-card { position:relative; overflow:hidden; transition: transform 0.25s ease, border-color 0.25s ease; }
  .feature-card:hover { transform: translateY(-3px); border-color: rgba(255,107,0,0.25) !important; }
  .feature-card:hover::after {
    content:''; position:absolute; top:0; left:-100%; width:60%; height:100%;
    background:linear-gradient(90deg,transparent,rgba(255,107,0,0.04),transparent);
    animation: cardShine 0.7s ease forwards;
  }
  .step-card { transition: transform 0.25s ease, border-color 0.25s ease; }
  .step-card:hover { transform: translateY(-4px); border-color: rgba(255,107,0,0.3) !important; }
  .cta-btn { transition: transform 0.15s ease, box-shadow 0.15s ease; }
  .cta-btn:hover { transform: scale(1.03); box-shadow: 0 0 0 1px rgba(255,107,0,0.6),0 8px 40px rgba(255,107,0,0.5),0 2px 6px rgba(0,0,0,0.4) !important; }
  .cta-btn:active { transform: scale(0.98); }
  .secondary-btn { transition: background 0.15s ease, border-color 0.15s ease; }
  .secondary-btn:hover { background: rgba(255,255,255,0.09) !important; border-color: rgba(255,255,255,0.2) !important; }
  .nav-pill { backdrop-filter: blur(20px) saturate(180%); }
  .tool-link { transition: all 0.2s; }
  .tool-link:hover { border-color: rgba(255,107,0,0.4) !important; color: #fff !important; background: rgba(255,107,0,0.05) !important; }
  @media(max-width:768px){
    .hide-mobile{display:none!important}
    .hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; padding: 32px 20px !important; }
    .hero-copy { max-width: 100% !important; }
    .bento-grid { grid-template-columns: 1fr !important; }
    .bento-card-span2 { grid-column: span 1 !important; }
    .stats-grid { grid-template-columns: repeat(2,1fr) !important; }
    .india-grid { grid-template-columns: repeat(2,1fr) !important; }
  }
  .show-mobile { display: none !important; }
  @media(max-width:768px){
    .show-mobile { display: flex !important; }
  }
  @media(max-width:430px){
    .cta-btn { height: 46px !important; padding: 0 18px !important; font-size: 14px !important; width: 100% !important; justify-content: center !important; }
    .secondary-btn { height: 46px !important; padding: 0 18px !important; font-size: 14px !important; width: 100% !important; justify-content: center !important; }
    .hero-grid { padding: 24px 16px !important; }
    .cta-wrap { flex-direction: column !important; }
  }
`;

/* ─── Utilities ─────────────────────────────────────────────────────── */
function FadeUp({ children, delay = 0, className = '', style = {} }) {
  return (
    <div className={className} style={{ animation: `fadeUp 0.6s ${delay}s cubic-bezier(.22,1,.36,1) both`, ...style }}>
      {children}
    </div>
  );
}

const S = {
  page:    { background: 'var(--black)', color: 'var(--text)', fontFamily: 'DM Sans, Outfit, sans-serif', fontWeight: 400, overflowX: 'hidden', minHeight: '100vh', position: 'relative' },
  eyebrow: { fontFamily: 'monospace', fontSize: 11, letterSpacing: '0.1em', color: 'var(--orange)', textTransform: 'uppercase', marginBottom: 12 },
  h2:      { fontFamily: 'Cabinet Grotesk, Outfit, sans-serif', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.08 },
  section: { position: 'relative', zIndex: 1, padding: '80px 24px', maxWidth: 1100, margin: '0 auto' },
};

/* ─── Nav ───────────────────────────────────────────────────────────── */
function Nav() {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const links = [
    ['Features',    '#features'],
    ['How it works','#how-it-works'],
    ['Tools',       '/tools'],
    ["What's New",  '/v1-release.html'],
  ];

  // Track viewport width to reliably show/hide elements
  useState(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  });

  return (
    <>
      <nav className="nav-pill" style={{ position: 'fixed', top: 54, left: '50%', transform: 'translateX(-50%)', width: 'min(960px, calc(100% - 32px))', background: 'rgba(13,17,23,0.85)', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: isMobile ? '10px 16px' : '12px 24px', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', animation: 'fadeUp 0.6s ease both' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', flexShrink: 0 }}>
          <div style={{ padding: 7, background: 'rgba(255,107,0,0.15)', border: '0.5px solid rgba(255,107,0,0.3)', borderRadius: 10 }}><Wallet size={18} color="#FF6B00" /></div>
          {!isMobile && <span style={{ fontFamily: 'Cabinet Grotesk, Outfit, sans-serif', fontWeight: 700, color: '#fff', fontSize: 17 }}>Budget Mantra</span>}
        </Link>
        {!isMobile && (
          <div style={{ display: 'flex', gap: 28 }}>
            {links.map(([l, h]) => (
              <a key={l} href={h} target={h.endsWith('.html') ? '_blank' : undefined} rel={h.endsWith('.html') ? 'noopener noreferrer' : undefined} style={{ color: 'var(--muted)', fontSize: 14, textDecoration: 'none', transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = '#fff'} onMouseLeave={e => e.target.style.color = 'var(--muted)'}>{l}</a>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Link to="/login" style={{ color: '#fff', fontSize: 14, textDecoration: 'none', padding: '8px 12px', whiteSpace: 'nowrap' }}>Login</Link>
          {!isMobile && (
            <Link to="/signup" className="cta-btn" style={{ background: 'var(--orange)', color: '#fff', fontSize: 14, fontWeight: 600, padding: '8px 18px', borderRadius: 10, textDecoration: 'none', boxShadow: '0 0 20px rgba(255,107,0,0.3)' }}>Get Started</Link>
          )}
          {isMobile && (
            <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          )}
        </div>
      </nav>
      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99, background: 'rgba(5,8,15,0.97)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 32 }}>
          <button onClick={() => setOpen(false)} style={{ position: 'absolute', top: 24, right: 24, background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={28} /></button>
          {links.map(([l, h]) => (
            <a key={l} href={h} onClick={() => setOpen(false)} style={{ color: '#fff', fontSize: 22, fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 700, textDecoration: 'none' }}>{l}</a>
          ))}
          <Link to="/signup" onClick={() => setOpen(false)} style={{ background: 'var(--orange)', color: '#fff', padding: '14px 40px', borderRadius: 14, fontWeight: 700, fontSize: 18, textDecoration: 'none' }}>Get Started Free</Link>
        </div>
      )}
    </>
  );
}

/* ─── Trust marquee ─────────────────────────────────────────────────── */
function TrustBar() {
  const items = [
    'Just type "swiggy 450" — Chanakya logs it instantly.',
    'We never ask for your bank login, UPI, or card. Zero access.',
    '26 free calculators. No signup needed. Ever.',
    'EMIs, goals, gold, hand loans — all in one place.',
    'Built for Indian salaries — lakhs, crores, HRA, EPF.',
    'Story mode or form mode. Your pace, your choice.',
  ];
  const all = [...items, ...items];
  return (
    <div style={{ background: 'var(--surface)', borderTop: '0.5px solid var(--border)', borderBottom: '0.5px solid var(--border)', padding: '14px 0', overflow: 'hidden' }}>
      <div style={{ display: 'flex', width: 'max-content', animation: 'marquee 40s linear infinite' }}>
        {all.map((t, i) => (
          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 48, whiteSpace: 'nowrap' }}>
            <span style={{ color: 'var(--orange)', fontWeight: 700, fontSize: 16 }}>·</span>
            <span style={{ fontSize: 13, color: 'var(--muted)', fontFamily: 'DM Sans, sans-serif' }}>{t}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── Stats bar ─────────────────────────────────────────────────────── */
function StatsBar() {
  const stats = [
    { val: '26',   label: 'Free tools',           sub: 'No signup needed' },
    { val: '10+',  label: 'Financial trackers',   sub: 'EMI, Goals, Gold, Loans…' },
    { val: '0',    label: 'Bank access required', sub: 'Your data stays yours' },
    { val: '₹0',   label: 'Forever free',         sub: 'Core features always free' },
  ];
  return (
    <section style={{ position: 'relative', zIndex: 1, borderTop: '0.5px solid var(--border)', borderBottom: '0.5px solid var(--border)' }}>
      <div className="stats-grid" style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)' }}>
        {stats.map((s, i) => (
          <FadeUp key={s.label} delay={i * 0.08} style={{ textAlign: 'center', padding: '32px 20px', borderRight: i < 3 ? '0.5px solid var(--border)' : 'none' }}>
            <div style={{ fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 800, fontSize: 'clamp(28px,4vw,40px)', color: 'var(--orange)', lineHeight: 1.1 }}>{s.val}</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#fff', marginTop: 6 }}>{s.label}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{s.sub}</div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

/* ─── How it works ──────────────────────────────────────────────────── */
const STEPS = [
  { num: '01', icon: MessageSquare, title: 'Type anything', body: 'Just tell Chanakya what you spent, earned, or owe. No forms. No categories. Plain English or Hinglish.', chips: ['"swiggy 340"', '"paid rent 22k"', '"salary 85000"'] },
  { num: '02', icon: Brain,         title: 'Chanakya understands', body: 'Reads intent, categorises automatically, logs the entry, and tells you what it noticed. No buttons. No dropdowns.', chips: [] },
  { num: '03', icon: TrendingUp,    title: 'Your money gets clear', body: 'Dashboard, EMI tracker, goals, investments — all updated instantly. One conversation does it all.', chips: [] },
];

/* ─── Features bento ────────────────────────────────────────────────── */
const FEATURES = [
  { col: 2, title: 'The month you finally knew where it all went',       cat: 'Budget',      desc: 'Category budgets that pace against the month. Chanakya catches the leak before it empties you.', stat: '23 spending categories', icon: BarChart3,  color: '#10b981' },
  { col: 1, title: 'The day you closed your EMI 4 months early',         cat: 'EMI',         desc: '"Pay ₹20K extra this month, save ₹31,420 in interest." Chanakya shows the exact number — you decide when.', stat: 'Full amortisation table', icon: Receipt,    color: '#6366f1' },
  { col: 1, title: 'The conversation that changed how you see money',     cat: 'Chanakya AI', desc: 'Not a chatbot. An advisor who speaks Hinglish, knows your real finances, and never lets you lie about the Zomato spend.', stat: 'Always on · Never judges', icon: Sparkles,   color: '#f97316', highlight: true },
  { col: 2, title: 'The morning you saw your entire net worth in one screen', cat: 'Wealth',  desc: 'Stocks, mutual funds, gold, FDs — all of it, live. Chanakya tells you when gold is up 8% and your allocation is still at 4%.', stat: '9 asset classes, live prices', icon: TrendingUp, color: '#0ea5e9' },
  { col: 1, title: 'The trip where nobody fought about splitting',        cat: 'Travel',      desc: 'Add expenses as you go, split by exact share or percentage, settle in one tap. Budget before you leave.', stat: 'Split by % or exact amount', icon: Plane, color: '#22c55e' },
  { col: 1, title: 'The upload that found ₹40,000 you forgot',           cat: 'Statements',  desc: 'Drop your bank PDF. Chanakya reads every transaction, auto-categorises it, and surfaces what you missed.', stat: 'Auto-categorised instantly', icon: Shield,     color: '#8b5cf6' },
];

/* ─── India section ─────────────────────────────────────────────────── */
const INDIA_ITEMS = [
  { emoji: '🏦', title: 'EPF & PPF',     body: 'Free calculators for EPF, PPF, NPS, SSY. Know exactly what your retirement corpus looks like.' },
  { emoji: '🥇', title: 'Gold tracker',  body: 'Physical gold, SGB, Gold ETF — all tracked live against MCX prices. Chanakya tells you when to buy.' },
  { emoji: '🧾', title: 'HRA & 80C',     body: 'Old regime vs new regime comparison. HRA exemption, 80C, 80D — Chanakya picks the better regime for your numbers.' },
  { emoji: '💸', title: 'EMI calendar',  body: 'Every loan, every debit day, every prepayment opportunity. Chanakya catches the EMI due tomorrow before your bank does.' },
  { emoji: '🤝', title: 'Hand loans',    body: '"Lent 5000 to Rahul, he owes by May" — tracked, reminded, and settled without the awkward WhatsApp forward.' },
  { emoji: '✈️', title: 'Trip budgets',  body: 'Plan a trip, split expenses with friends, settle in one tap. No more "who paid for the cab?" debates.' },
];

/* ─── Chanakya demo conversations ───────────────────────────────────── */
const DEMO_CHATS = [
  { user: 'swiggy 450',              bot: '₹450 logged under Food & Dining. You\'ve spent ₹3,840 on food this month — 76% of budget with 12 days left. Ease up on the cloud kitchens this week.' },
  { user: 'how am I doing this month?', bot: 'You\'re in solid shape — ₹18,400 spent of ₹25,000 with 10 days left. Food ran hot at 89% but everything else is on track. You can afford the weekend trip without dipping into savings.' },
  { user: 'should I prepay my home loan this month?', bot: 'You have ₹15,000 free cash. Prepaying saves ₹3,840 in interest over the remaining tenure. EMI stays the same but you close 2 months early. Yes — do it.' },
];

/* ─── FAQ ───────────────────────────────────────────────────────────── */
const FAQS = [
  { q: 'Is Budget Mantra free?',         a: 'Yes. The core app — tracking, Chanakya AI, calculators, EMI tracker, goals — is free forever. No credit card, no trial period, no hidden fees.' },
  { q: 'Will you ever sell my data?',    a: 'No. Full stop. We do not sell, rent, trade, or share your financial data with advertisers, data brokers, or any third party. Your data is used only to run the app for you. We make money through referrals for products you choose (credit cards, loans) — never by selling what you tell us.' },
  { q: 'Does it need my bank login or UPI access?', a: 'Never. Budget Mantra never asks for your bank password, UPI PIN, card number, OTP, or any bank credential. You either type expenses to Chanakya or upload your bank PDF — we process it locally and never store credentials.' },
  { q: 'Can I delete my account and all my data?', a: 'Yes, any time, in one tap. Go to Profile → Delete Account. All your transactions, goals, and history are permanently erased from our servers within 24 hours. No recovery, no retention.' },
  { q: 'Is there an Android app?',       a: 'Yes — download the free APK directly from our website. It works on any Android device. iOS is in progress.' },
  { q: 'What is Chanakya?',              a: 'Chanakya is the AI advisor built into Budget Mantra. You talk to it like a friend — "swiggy 450" logs an expense, "how am I doing?" gives you your financial story, "should I prepay my home loan?" gives you the actual numbers. It uses Claude AI and knows your real financial data.' },
  { q: 'What calculators do you have?',  a: 'EMI, SIP, FD, PPF, FIRE, Income Tax, HRA, EPF, SSY, Gratuity, Stamp Duty, Capital Gains, XIRR, Lumpsum, Salary, Car Loan, Home Loan Prepay, Step-up SIP, Home Affordability, Rent vs Buy, Rent Receipt, Rental Agreement, and more. All free, no login needed.' },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '0.5px solid var(--border)', padding: '0' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', background: 'none', border: 'none', color: '#fff', textAlign: 'left', padding: '20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: 15, fontWeight: 600, gap: 16 }}>
        {q}
        {open ? <ChevronUp size={18} color="var(--orange)" style={{ shrink: 0 }} /> : <ChevronDown size={18} color="var(--muted)" style={{ shrink: 0 }} />}
      </button>
      {open && <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, paddingBottom: 20, margin: 0 }}>{a}</p>}
    </div>
  );
}

/* ─── Main ──────────────────────────────────────────────────────────── */
export default function LandingPage() {
  return (
    <div style={S.page}>
      <style>{PAGE_VARS}</style>
      <ParticleField />

      {/* ── ANNOUNCEMENT BAR ── */}
      <a href="/v1-release.html" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: 'linear-gradient(90deg,rgba(255,107,0,0.12),rgba(255,107,0,0.07))', borderBottom: '0.5px solid rgba(255,107,0,0.2)', padding: '9px 20px', textDecoration: 'none', position: 'relative', zIndex: 101 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--orange)', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4 }}>v1.0</span>
        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', fontFamily: 'DM Sans, sans-serif' }}>Budget Mantra v1.0 is live — Chanakya v2, Story Mode calculators &amp; more</span>
        <span style={{ fontSize: 13, color: 'var(--orange)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>Read release notes →</span>
      </a>

      <Nav />

      {/* ── HERO ── */}
      <section style={{ position: 'relative', zIndex: 1, minHeight: '100vh', display: 'flex', alignItems: 'center', paddingTop: 128 }}>
        <div style={{ position: 'absolute', top: '10%', right: '5%', width: 600, height: 600, background: 'radial-gradient(ellipse, rgba(255,107,0,0.1) 0%, transparent 70%)', pointerEvents: 'none', borderRadius: '50%' }} />
        <div style={{ position: 'absolute', bottom: '5%', left: '0%', width: 400, height: 400, background: 'radial-gradient(ellipse, rgba(251,191,36,0.07) 0%, transparent 70%)', pointerEvents: 'none', borderRadius: '50%' }} />

        <div className="hero-grid" style={{ maxWidth: 1200, margin: '0 auto', padding: '60px 24px', width: '100%', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 64, alignItems: 'center' }}>
          <div className="hero-copy" style={{ maxWidth: 560 }}>
            <div className="fade-up" style={{ animationDelay: '0.2s', display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', background: 'rgba(255,107,0,0.1)', border: '0.5px solid rgba(255,107,0,0.3)', borderRadius: 100, marginBottom: 24 }}>
              <Sparkles size={13} color="var(--orange)" />
              <span style={{ fontSize: 13, color: 'rgba(255,107,0,0.9)' }}>India's AI money advisor — speaks Hinglish</span>
            </div>

            <h1 className="fade-up" style={{ animationDelay: '0.35s', fontFamily: 'Cabinet Grotesk, Outfit, sans-serif', fontWeight: 800, fontSize: 'clamp(42px,6vw,76px)', lineHeight: 1.0, letterSpacing: '-0.03em', color: '#fff', marginBottom: 24 }}>
              Meet{' '}
              <span style={{ color: 'var(--orange)', textShadow: '0 0 40px rgba(255,107,0,0.4)' }}>Chanakya.</span>
              <br />Your money's<br />new boss.
            </h1>

            <p className="fade-up" style={{ animationDelay: '0.5s', fontSize: 17, color: 'var(--muted)', lineHeight: 1.7, maxWidth: 460, marginBottom: 28 }}>
              No spreadsheets. No boring dashboards. Just type{' '}
              <span style={{ background: 'rgba(255,107,0,0.12)', border: '0.5px solid rgba(255,107,0,0.25)', borderRadius: 6, padding: '2px 8px', color: 'var(--orange)', fontFamily: 'monospace', fontSize: 14 }}>swiggy 450</span>
              {' '}and Chanakya logs it, tells you what it means, and shows you what to do next.
            </p>

            <div className="fade-up" style={{ animationDelay: '0.6s', marginBottom: 28 }}>
              <p style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, fontFamily: 'monospace' }}>Your story with Chanakya</p>
              {[
                { day: 'Day 1',   story: 'You type "swiggy 450" — Chanakya logs it.',     color: 'var(--muted)' },
                { day: 'Week 4',  story: '₹4,820 food leak found. First SIP started.',    color: '#f97316' },
                { day: 'Month 6', story: 'EMI closing 4 months early. ₹18K saved. 🎯',   color: '#22c55e' },
              ].map(({ day, story, color }, i) => (
                <div key={day} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, position: 'relative', paddingBottom: i < 2 ? 14 : 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, paddingTop: 3 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 999, background: color, boxShadow: `0 0 8px ${color}80` }} />
                    {i < 2 && <div style={{ width: 1, flex: 1, background: 'rgba(255,255,255,0.08)', marginTop: 4, minHeight: 20 }} />}
                  </div>
                  <div>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 2 }}>{day}</span>
                    <span style={{ fontSize: 13, color: '#fff', lineHeight: 1.5 }}>{story}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="fade-up cta-wrap" style={{ animationDelay: '0.7s', display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
              <Link to="/signup" className="cta-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 52, padding: '0 28px', background: 'var(--orange)', borderRadius: 14, color: '#fff', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 700, fontSize: 16, textDecoration: 'none', boxShadow: '0 0 0 1px rgba(255,107,0,0.5),0 4px 24px rgba(255,107,0,0.35)' }}>
                Start Free Today <ArrowRight size={18} />
              </Link>
              <a href="#features" className="secondary-btn" style={{ display: 'inline-flex', alignItems: 'center', height: 52, padding: '0 24px', background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.12)', borderRadius: 14, color: '#fff', fontSize: 16, textDecoration: 'none' }}>
                See Features
              </a>
            </div>

            <div className="fade-up" style={{ animationDelay: '0.8s', display: 'flex', flexWrap: 'wrap', gap: 14 }}>
              {['No credit card required', 'Free forever plan', 'No bank access ever'].map(t => (
                <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)' }}>
                  <CheckCircle size={12} color="#22c55e" /> {t}
                </span>
              ))}
            </div>
          </div>

          <div className="fade-up" style={{ animationDelay: '0.4s', display: 'flex', justifyContent: 'center', paddingBottom: 40 }}>
            <ChatMockup3D />
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ── */}
      <TrustBar />

      {/* ── STATS ── */}
      <StatsBar />

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" style={S.section}>
        <FadeUp style={{ textAlign: 'center', marginBottom: 60 }}>
          <div style={S.eyebrow}>How it works</div>
          <h2 style={{ ...S.h2, fontSize: 'clamp(36px,5vw,56px)' }}>Three words. Total control.</h2>
        </FadeUp>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {STEPS.map((st, i) => {
            const Icon = st.icon;
            return (
              <FadeUp key={st.num} delay={i * 0.12}>
                <div className="step-card" style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 20, padding: '32px 28px', position: 'relative', overflow: 'hidden', height: '100%' }}>
                  <div style={{ fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 800, fontSize: 80, color: 'rgba(255,107,0,0.06)', position: 'absolute', top: -10, left: 16, lineHeight: 1, userSelect: 'none' }}>{st.num}</div>
                  <div style={{ width: 44, height: 44, background: 'rgba(255,107,0,0.1)', border: '0.5px solid rgba(255,107,0,0.2)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, position: 'relative' }}>
                    <Icon size={20} color="var(--orange)" />
                  </div>
                  <div style={{ fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 700, fontSize: 22, color: '#fff', marginBottom: 12 }}>{st.title}</div>
                  <div style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.7 }}>{st.body}</div>
                  {st.chips.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
                      {st.chips.map(c => <span key={c} style={{ background: 'rgba(255,107,0,0.08)', border: '0.5px solid rgba(255,107,0,0.2)', borderRadius: 8, padding: '4px 10px', fontFamily: 'monospace', fontSize: 12, color: 'var(--orange)' }}>{c}</span>)}
                    </div>
                  )}
                </div>
              </FadeUp>
            );
          })}
        </div>
      </section>

      {/* ── CHANAKYA DEMO CONVERSATIONS ── */}
      <section style={{ ...S.section, maxWidth: 900 }}>
        <FadeUp style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={S.eyebrow}>See it in action</div>
          <h2 style={{ ...S.h2, fontSize: 'clamp(28px,4vw,44px)' }}>Real conversations. Real answers.</h2>
          <p style={{ fontSize: 16, color: 'var(--muted)', marginTop: 12 }}>This is what talking to Chanakya actually looks like.</p>
        </FadeUp>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {DEMO_CHATS.map((chat, i) => (
            <FadeUp key={i} delay={i * 0.1}>
              <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 20, padding: '20px 24px' }}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(255,107,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 14 }}>👤</span>
                  </div>
                  <div style={{ background: 'rgba(255,107,0,0.08)', border: '0.5px solid rgba(255,107,0,0.2)', borderRadius: 12, padding: '10px 14px', fontFamily: 'monospace', fontSize: 14, color: 'var(--orange)' }}>{chat.user}</div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 10, background: '#b45309', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: '#fef3c7' }}>₹</span>
                  </div>
                  <div style={{ flex: 1, background: 'var(--surface-2)', border: '0.5px solid var(--border)', borderRadius: 12, padding: '10px 14px', fontSize: 14, color: 'var(--text)', lineHeight: 1.6 }}>{chat.bot}</div>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp style={{ textAlign: 'center', marginTop: 32 }}>
          <Link to="/signup" className="cta-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 48, padding: '0 28px', background: 'var(--orange)', borderRadius: 12, color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
            Talk to Chanakya free <ArrowRight size={16} />
          </Link>
        </FadeUp>
      </section>

      {/* ── FEATURES BENTO ── */}
      <section id="features" style={S.section}>
        <FadeUp style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={S.eyebrow}>Everything inside</div>
          <h2 style={{ ...S.h2, fontSize: 'clamp(32px,5vw,52px)' }}>Every feature tells your story.</h2>
          <p style={{ fontSize: 16, color: 'var(--muted)', marginTop: 12, maxWidth: 500, margin: '12px auto 0' }}>Not a list of tools. Moments that make your money life make sense.</p>
        </FadeUp>
        <div className="bento-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={f.title} style={{ gridColumn: `span ${f.col}` }} className={f.col === 2 ? 'bento-card-span2' : ''}>
                <FadeUp delay={i * 0.08} className="feature-card">
                  <div style={{ background: 'var(--surface)', height: '100%', border: f.highlight ? '0.5px solid rgba(255,107,0,0.25)' : '0.5px solid var(--border)', borderRadius: 20, padding: '28px 24px', overflow: 'hidden', position: 'relative', boxShadow: f.highlight ? '0 0 40px rgba(255,107,0,0.05)' : 'none' }}>
                    <span style={{ fontSize: 11, color: 'var(--muted)', background: 'var(--surface-2)', border: '0.5px solid var(--border)', borderRadius: 6, padding: '3px 8px', display: 'inline-block', marginBottom: 16 }}>{f.cat}</span>
                    <div style={{ width: 40, height: 40, background: `${f.color}20`, border: `0.5px solid ${f.color}40`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                      <Icon size={18} color={f.color} />
                    </div>
                    <div style={{ fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 700, fontSize: 20, color: '#fff', marginBottom: 10 }}>{f.title}</div>
                    <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 20 }}>{f.desc}</div>
                    <div style={{ fontSize: 12, color: f.highlight ? 'var(--orange)' : 'var(--muted)', fontFamily: 'monospace' }}>→ {f.stat}</div>
                  </div>
                </FadeUp>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── BUILT FOR INDIA ── */}
      <section style={{ ...S.section, background: 'linear-gradient(to bottom, transparent, rgba(255,107,0,0.03), transparent)', padding: '80px 24px' }}>
        <FadeUp style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={S.eyebrow}>Made in India 🇮🇳</div>
          <h2 style={{ ...S.h2, fontSize: 'clamp(28px,4vw,44px)' }}>Built for how Indians actually handle money.</h2>
          <p style={{ fontSize: 16, color: 'var(--muted)', marginTop: 12, maxWidth: 560, margin: '12px auto 0' }}>
            Not a Western app translated to ₹. Every feature built for EPF, HRA, gold, hand loans, lakhs, and crores.
          </p>
        </FadeUp>
        <div className="india-grid" style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
          {INDIA_ITEMS.map((item, i) => (
            <FadeUp key={item.title} delay={i * 0.07}>
              <div className="step-card" style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 18, padding: '22px 20px', height: '100%' }}>
                <span style={{ fontSize: 28, display: 'block', marginBottom: 12 }}>{item.emoji}</span>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#fff', marginBottom: 8 }}>{item.title}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>{item.body}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ── FREE TOOLS ── */}
      <section style={S.section}>
        <FadeUp style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={S.eyebrow}>Free calculators</div>
          <h2 style={{ ...S.h2, fontSize: 'clamp(28px,4vw,44px)' }}>26 tools. Free. No signup needed.</h2>
          <p style={{ fontSize: 16, color: 'var(--muted)', marginTop: 12, maxWidth: 560, margin: '12px auto 0' }}>
            Every calculator updates in real time and tells you what the number means — not just what it is.
          </p>
        </FadeUp>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
          {[
            { label: 'SIP Calculator',          path: '/tools/sip-calculator',                emoji: '📈' },
            { label: 'EMI Calculator',           path: '/tools/emi-calculator',                emoji: '🏦' },
            { label: 'Income Tax',               path: '/tools/income-tax-calculator',         emoji: '💸' },
            { label: 'FD Calculator',            path: '/tools/fd-calculator',                 emoji: '🔒' },
            { label: 'FIRE Calculator',          path: '/tools/fire-calculator',               emoji: '🔥' },
            { label: 'Rent vs Buy',              path: '/tools/rent-vs-buy-calculator',        emoji: '🏠' },
            { label: 'HRA Calculator',           path: '/tools/hra-calculator',                emoji: '🏢' },
            { label: 'PPF Calculator',           path: '/tools/ppf-calculator',                emoji: '📊' },
            { label: 'Home Affordability',       path: '/tools/home-affordability-calculator', emoji: '🏡' },
            { label: 'Salary Calculator',        path: '/tools/salary-calculator',             emoji: '💼' },
            { label: 'Education Loan',           path: '/tools/education-loan-calculator',     emoji: '🎓' },
            { label: 'Credit Card Trap',         path: '/tools/credit-card-trap',              emoji: '💳' },
            { label: 'EPF Calculator',           path: '/tools/epf-calculator',                emoji: '🏛️' },
            { label: 'Gratuity Calculator',      path: '/tools/gratuity-calculator',           emoji: '🎖️' },
            { label: 'XIRR Calculator',          path: '/tools/xirr-calculator',               emoji: '📉' },
            { label: 'SSY Calculator',           path: '/tools/ssy-calculator',                emoji: '👧' },
            { label: 'Step-up SIP',              path: '/tools/step-up-sip-calculator',        emoji: '⬆️' },
            { label: 'Home Loan Prepay',         path: '/tools/home-loan-prepay-calculator',   emoji: '💰' },
            { label: 'Lumpsum Calculator',       path: '/tools/lumpsum-calculator',            emoji: '💎' },
            { label: 'Capital Gains Tax',        path: '/tools/capital-gains-tax',             emoji: '📋' },
            { label: 'Stamp Duty',               path: '/tools/stamp-duty-calculator',         emoji: '🖨' },
            { label: 'Car Loan',                 path: '/tools/car-loan-calculator',           emoji: '🚗' },
            { label: 'Rent Receipt',             path: '/tools/rent-receipt-generator',        emoji: '🧾' },
            { label: 'Rental Agreement',         path: '/tools/rental-agreement',              emoji: '📝' },
            { label: 'Rent vs Buy (detailed)',   path: '/tools/rent-vs-buy',                   emoji: '🔍' },
          ].map((tool) => (
            <Link key={tool.path} to={tool.path} className="tool-link" style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 14, padding: '13px 14px', textDecoration: 'none', color: 'var(--muted)', fontSize: 13 }}>
              <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>{tool.emoji}</span>
              <span style={{ flex: 1, lineHeight: 1.3 }}>{tool.label}</span>
              <span style={{ opacity: 0.3, fontSize: 10 }}>→</span>
            </Link>
          ))}
        </div>
        <FadeUp style={{ textAlign: 'center', marginTop: 32 }}>
          <Link to="/tools" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'transparent', border: '0.5px solid rgba(255,107,0,0.4)', borderRadius: 100, padding: '12px 28px', textDecoration: 'none', color: 'var(--orange)', fontSize: 14, fontWeight: 600 }}>
            See all calculators <ArrowRight size={16} />
          </Link>
        </FadeUp>
      </section>

      {/* ── MOBILE APP ── */}
      <section style={{ ...S.section, maxWidth: 1100 }}>
        <FadeUp>
          <div style={{ background: 'linear-gradient(135deg, rgba(255,107,0,0.12) 0%, rgba(255,107,0,0.04) 100%)', border: '0.5px solid rgba(255,107,0,0.25)', borderRadius: 24, padding: '48px 40px', display: 'flex', flexWrap: 'wrap', gap: 40, alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ maxWidth: 480 }}>
              <div style={S.eyebrow}>Android app</div>
              <h2 style={{ ...S.h2, fontSize: 'clamp(26px,4vw,40px)', marginBottom: 16 }}>Take Chanakya everywhere.</h2>
              <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 28 }}>
                The full Budget Mantra experience — offline-capable, push notifications, native date pickers, voice input — in your pocket. Free APK, no Play Store needed.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <a href="https://expo.dev/accounts/budgetmantra/projects/budget-mantra/builds" target="_blank" rel="noopener noreferrer" className="cta-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 48, padding: '0 24px', background: 'var(--orange)', borderRadius: 12, color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
                  <Download size={16} /> Download APK (Android)
                </a>
                <Link to="/signup" className="secondary-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 48, padding: '0 20px', background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.12)', borderRadius: 12, color: '#fff', fontSize: 14, textDecoration: 'none' }}>
                  Use on web →
                </Link>
              </div>
              <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 14 }}>Free · Works on any Android · Play Store coming soon</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 240 }}>
              {[
                { icon: '💬', label: 'Chanakya AI chat — voice + text' },
                { icon: '📅', label: 'EMI calendar with due-day alerts' },
                { icon: '🎯', label: 'Goals + savings tracker' },
                { icon: '🔔', label: 'Push alerts when budget hits 85%' },
                { icon: '✨', label: 'Story mode — one question at a time' },
              ].map(f => (
                <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, color: 'var(--muted)' }}>
                  <span style={{ fontSize: 18 }}>{f.icon}</span>
                  {f.label}
                </div>
              ))}
            </div>
          </div>
        </FadeUp>
      </section>

      {/* ── SECURITY ── */}
      <section style={S.section}>
        <FadeUp style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={S.eyebrow}>Privacy first</div>
          <h2 style={{ ...S.h2, fontSize: 'clamp(28px,4vw,44px)' }}>We never touch your bank.</h2>
        </FadeUp>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
          {[
            { icon: Lock,    color: '#22c55e', title: 'No bank login ever',      body: 'We never ask for your net banking password, UPI PIN, or any banking credential. Not now, not ever.' },
            { icon: Shield,  color: '#6366f1', title: 'No card data',            body: 'We don\'t ask for your card number, CVV, expiry, or OTP. Chanakya will never ask for these.' },
            { icon: Zap,     color: '#f59e0b', title: 'You type, we log',        body: 'You tell Chanakya what you spent. Or upload your bank PDF — we parse it and forget it. No raw data stored.' },
            { icon: CheckCircle, color: '#f97316', title: 'Your data is yours', body: 'You can delete your entire account and all data any time. No retention, no selling, no ads.' },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <FadeUp key={item.title} delay={i * 0.08}>
                <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 18, padding: '24px 20px' }}>
                  <div style={{ width: 40, height: 40, background: `${item.color}20`, border: `0.5px solid ${item.color}40`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                    <Icon size={18} color={item.color} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 8 }}>{item.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>{item.body}</div>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </section>

      {/* ── Privacy Pledge ── */}
      <section style={{ ...S.section, maxWidth: 800 }}>
        <FadeUp>
          <div style={{
            background: 'linear-gradient(135deg, rgba(249,115,22,0.06) 0%, rgba(99,102,241,0.06) 100%)',
            border: '1px solid rgba(249,115,22,0.25)',
            borderRadius: 24,
            padding: 'clamp(28px, 4vw, 48px)',
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Background accent */}
            <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: 'radial-gradient(circle, rgba(249,115,22,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap', position: 'relative' }}>
              <div style={{ width: 48, height: 48, background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Shield size={22} color="#f97316" />
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#f97316', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>Our Privacy Pledge</div>
                <h3 style={{ margin: '0 0 16px', fontSize: 'clamp(20px,3vw,28px)', fontWeight: 800, color: '#fff', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                  We will never sell your data.<br />
                  <span style={{ color: '#f97316' }}>Ever.</span>
                </h3>
                <p style={{ margin: '0 0 20px', fontSize: 15, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7 }}>
                  Your income, expenses, EMIs, and goals are some of the most personal numbers in your life.
                  We treat them that way. Budget Mantra has <strong style={{ color: '#fff' }}>no ads, no data brokers, no selling to third parties</strong> — period.
                  We make money when we help you find a better credit card or loan deal, and only if you choose it.
                  Your data powers your story, not our revenue.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {[
                    { icon: '🚫', text: 'No ads, ever' },
                    { icon: '🔒', text: 'No data sold' },
                    { icon: '🗑️', text: 'Delete any time' },
                    { icon: '🏦', text: 'No bank credentials' },
                    { icon: '📵', text: 'No OTP, no UPI PIN' },
                  ].map(item => (
                    <span key={item.text} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.12)', borderRadius: 100, padding: '6px 14px', fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>
                      {item.icon} {item.text}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* ── FAQ ── */}
      <section style={{ ...S.section, maxWidth: 760 }}>
        <FadeUp style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={S.eyebrow}>FAQ</div>
          <h2 style={{ ...S.h2, fontSize: 'clamp(28px,4vw,40px)' }}>Common questions.</h2>
        </FadeUp>
        <FadeUp>
          <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 20, padding: '0 28px' }}>
            {FAQS.map(faq => <FAQItem key={faq.q} {...faq} />)}
          </div>
        </FadeUp>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ position: 'relative', zIndex: 1, padding: '120px 24px', textAlign: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(255,107,0,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <FadeUp style={{ maxWidth: 700, margin: '0 auto', position: 'relative' }}>
          <p style={{ fontSize: 14, color: 'var(--muted)', letterSpacing: '0.05em', marginBottom: 20 }}>Free forever · No credit card · Start in 30 seconds</p>
          <h2 style={{ ...S.h2, fontSize: 'clamp(32px,5vw,52px)', marginBottom: 20 }}>Your story starts with<br />one conversation.</h2>
          <p style={{ fontSize: 17, color: 'var(--muted)', marginBottom: 40, lineHeight: 1.7 }}>
            "swiggy 450" is the first line. The last line is the one where you retire early, fund the trip, or close the EMI for good.
          </p>
          <Link to="/signup" className="cta-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, height: 60, padding: '0 40px', background: 'var(--orange)', borderRadius: 16, color: '#fff', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 700, fontSize: 18, textDecoration: 'none', boxShadow: '0 0 0 1px rgba(255,107,0,0.4),0 8px 40px rgba(255,107,0,0.4)' }}>
            Start talking to Chanakya <ArrowRight size={20} />
          </Link>
          <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 20, marginTop: 28 }}>
            {['No credit card required', 'Free forever plan', 'No bank access ever'].map(t => (
              <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--muted)' }}>
                <CheckCircle size={13} color="#22c55e" /> {t}
              </span>
            ))}
          </div>
        </FadeUp>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: 'var(--surface)', borderTop: '0.5px solid var(--border)', padding: '48px 24px 32px', position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          {/* Top row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 40, marginBottom: 48 }}>
            <div>
              <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', marginBottom: 12 }}>
                <div style={{ padding: 8, background: 'rgba(255,107,0,0.15)', borderRadius: 10 }}><Wallet size={18} color="var(--orange)" /></div>
                <span style={{ fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 700, color: '#fff', fontSize: 17 }}>Budget Mantra</span>
              </Link>
              <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>India's AI-powered personal finance app. Built for how Indians actually handle money.</p>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>Made with ❤️ in India 🇮🇳</span>
            </div>
            <div>
              <p style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16, fontFamily: 'monospace' }}>Product</p>
              {[['Features', '#features'], ['How it works', '#how-it-works'], ['Android App', 'https://expo.dev/accounts/budgetmantra/projects/budget-mantra/builds'], ['Chanakya AI', '/signup']].map(([l, h]) => (
                <a key={l} href={h} style={{ display: 'block', color: 'var(--muted)', fontSize: 13, textDecoration: 'none', marginBottom: 10, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.target.style.color = '#fff'} onMouseLeave={e => e.target.style.color = 'var(--muted)'}>{l}</a>
              ))}
            </div>
            <div>
              <p style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16, fontFamily: 'monospace' }}>Tools</p>
              {[['All Calculators', '/tools'], ['EMI Calculator', '/tools/emi-calculator'], ['SIP Calculator', '/tools/sip-calculator'], ['FIRE Calculator', '/tools/fire-calculator'], ['Income Tax', '/tools/income-tax-calculator'], ['HRA Calculator', '/tools/hra-calculator']].map(([l, h]) => (
                <Link key={l} to={h} style={{ display: 'block', color: 'var(--muted)', fontSize: 13, textDecoration: 'none', marginBottom: 10, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.target.style.color = '#fff'} onMouseLeave={e => e.target.style.color = 'var(--muted)'}>{l}</Link>
              ))}
            </div>
            <div>
              <p style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16, fontFamily: 'monospace' }}>Learn</p>
              {[['Financial Guides', '/learn'], ['How AI Works', '/learn/how-ai-works']].map(([l, h]) => (
                <Link key={l} to={h} style={{ display: 'block', color: 'var(--muted)', fontSize: 13, textDecoration: 'none', marginBottom: 10, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.target.style.color = '#fff'} onMouseLeave={e => e.target.style.color = 'var(--muted)'}>{l}</Link>
              ))}
            </div>
            <div>
              <p style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16, fontFamily: 'monospace' }}>Account</p>
              {[['Sign up free', '/signup'], ['Log in', '/login'], ['Privacy policy', '/privacy'], ['Terms of service', '/terms']].map(([l, h]) => (
                <Link key={l} to={h} style={{ display: 'block', color: 'var(--muted)', fontSize: 13, textDecoration: 'none', marginBottom: 10, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.target.style.color = '#fff'} onMouseLeave={e => e.target.style.color = 'var(--muted)'}>{l}</Link>
              ))}
            </div>
          </div>
          {/* Bottom row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'space-between', alignItems: 'center', borderTop: '0.5px solid var(--border)', paddingTop: 24 }}>
            <span style={{ color: 'var(--muted)', fontSize: 12 }}>© 2026 Budget Mantra. All rights reserved.</span>
            <span style={{ color: 'var(--muted)', fontSize: 12 }}>Not a registered investment advisor. Tools are for educational purposes only.</span>
          </div>
        </div>
      </footer>
      <ScrollToTop />
    </div>
  );
}
